// PartnerDashboard.jsx - Complete Partner Dashboard with Staff Assignment and Order Cancellation
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import useApi from "hooks/useApi";
import useAuth from "hooks/useAuth";
import useApp from "hooks/useApp";

import { 
    Card, 
    Button, 
    DataTable, 
    Column, 
    Dialog, 
    Toast, 
    TabView, 
    TabPanel,
    Chart,
    Calendar,
    Dropdown,
    InputText,
    InputTextarea,
    Badge,
    Tag,
    Rating,
    ProgressBar,
    Divider,
    Panel,
    Menu,
    ConfirmDialog,
    Timeline,
    Skeleton,
    Avatar,
    Chip,
    RadioButton,
    InputNumber
} from 'primereact';

const API_URL = process.env.REACT_APP_API_PATH;

const PartnerDashboard = () => {
    // State Management
    const auth = useAuth();
    const app = useApp();
    const api = useApi();

    const [loading, setLoading] = useState(true);
    const [partnerData, setPartnerData] = useState(null);
    const [bookings, setBookings] = useState([]);
    const [filteredBookings, setFilteredBookings] = useState([]);
    const [stats, setStats] = useState({
        totalBookings: 0,
        pendingBookings: 0,
        activeBookings: 0,
        completedBookings: 0,
        cancelledBookings: 0,
        totalRevenue: 0,
        pendingPayments: 0,
        avgRating: 0,
        monthlyGrowth: 0
    });
    
    const [notifications, setNotifications] = useState([]);
    const [payments, setPayments] = useState([]);
    const [showBookingDialog, setShowBookingDialog] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [dateRange, setDateRange] = useState({
        start: new Date(new Date().setDate(new Date().getDate() - 30)),
        end: new Date()
    });
    
    const [statusFilter, setStatusFilter] = useState('All');
    const [searchText, setSearchText] = useState('');
    const [chartData, setChartData] = useState(null);
    const [chartOptions, setChartOptions] = useState(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [actionType, setActionType] = useState('');
    const [actionBookingId, setActionBookingId] = useState(null);
    
    // Staff assignment states
    const [showStaffDialog, setShowStaffDialog] = useState(false);
    const [staffAssignmentType, setStaffAssignmentType] = useState('');
    const [availableStaff, setAvailableStaff] = useState([]);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [currentBookingForStaff, setCurrentBookingForStaff] = useState(null);
    const [loadingStaff, setLoadingStaff] = useState(false);
    
    // Cancellation states
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [cancelBookingId, setCancelBookingId] = useState(null);
    const [cancelReason, setCancelReason] = useState('');
    const [cancelReasonOther, setCancelReasonOther] = useState('');
    const [selectedCancelReason, setSelectedCancelReason] = useState('');
    const [cancellationNote, setCancellationNote] = useState('');
    const [refundAmount, setRefundAmount] = useState(0);
    const [processRefund, setProcessRefund] = useState(true);
    const [cancellationLoading, setCancellationLoading] = useState(false);
    
    const toast = useRef(null);
    
    // Status options
    const statusOptions = [
        { label: 'All Status', value: 'All' },
        { label: 'Pending', value: 'Pending' },
        { label: 'Confirmed', value: 'Confirmed' },
        { label: 'Picked Up', value: 'PickedUp' },
        { label: 'Processing', value: 'Processing' },
        { label: 'Ready for Delivery', value: 'ReadyForDelivery' },
        { label: 'Out for Delivery', value: 'OutForDelivery' },
        { label: 'Delivered', value: 'Delivered' },
        { label: 'Completed', value: 'Completed' },
        { label: 'Cancelled', value: 'Cancelled' }
    ];
    
    // Cancellation reasons
    const cancellationReasons = [
        { label: 'Customer Request', value: 'Customer Request' },
        { label: 'Service Unavailable', value: 'Service Unavailable' },
        { label: 'Capacity Issue', value: 'Capacity Issue' },
        { label: 'Staff Unavailable', value: 'Staff Unavailable' },
        { label: 'Location Issue', value: 'Location Issue' },
        { label: 'Payment Issue', value: 'Payment Issue' },
        { label: 'Duplicate Booking', value: 'Duplicate Booking' },
        { label: 'Schedule Conflict', value: 'Schedule Conflict' },
        { label: 'Other', value: 'Other' }
    ];
    
    // Status colors
    const statusColors = {
        'Pending': 'warning',
        'Confirmed': 'info',
        'PickedUp': 'primary',
        'Processing': 'secondary',
        'ReadyForDelivery': 'help',
        'OutForDelivery': 'success',
        'Delivered': 'success',
        'Completed': 'success',
        'Cancelled': 'danger'
    };
    
    // Payment status colors
    const paymentColors = {
        'Pending': 'warning',
        'Paid': 'success',
        'Failed': 'danger',
        'Processing': 'info',
        'Refunded': 'secondary',
        'PartiallyRefunded': 'warning'
    };
    
    // ==================== INITIAL LOAD ====================
    
    useEffect(() => {
        loadPartnerData();
        initializeChart();
    }, []);
    
    useEffect(() => {
        if (!auth?.accessToken) return;
        if (!partnerData) return;
        loadDashboardData();
    }, [auth?.accessToken, partnerData, dateRange]);
    
    useEffect(() => {
        filterBookings();
    }, [bookings, statusFilter, searchText]);
    
    // ==================== API CALLS ====================
    
    const loadPartnerData = async () => {
        try {
            const response = await axios.get(
                `${API_URL}pdashboard/profile`,
                {
                    headers: {
                        Authorization: `Bearer ${auth.accessToken}`
                    }
                }
            );

            if (response.data.success) {
                setPartnerData(response.data.data);
            }
        } catch (error) {
            console.error("Error loading partner data:", error.response?.data || error);
            showError("Failed to load partner data");
        }
    };

    const loadDashboardData = async () => {
        try {
            if (!auth?.accessToken) return;

            setLoading(true);

            const headers = {
                Authorization: `Bearer ${auth.accessToken}`
            };

            const [bookingsRes, statsRes, notificationsRes, paymentsRes] = await Promise.all([
                axios.get(`${API_URL}pdashboard/bookingspartner`, {
                    headers,
                    params: {
                        startDate: dateRange.start.toISOString().split('T')[0],
                        endDate: dateRange.end.toISOString().split('T')[0]
                    }
                }),
                axios.get(`${API_URL}pdashboard/stats`, {
                    headers,
                    params: {
                        startDate: dateRange.start.toISOString().split('T')[0],
                        endDate: dateRange.end.toISOString().split('T')[0]
                    }
                }),
                axios.get(`${API_URL}pdashboard/notifications`, {
                    headers,
                    params: { unreadOnly: true }
                }),
                axios.get(`${API_URL}pdashboard/payments`, {
                    headers,
                    params: { limit: 5 }
                })
            ]);

            if (bookingsRes.data.success) {
                setBookings(bookingsRes.data.data);
                setFilteredBookings(bookingsRes.data.data);
            }

            if (statsRes.data.success) {
                setStats(statsRes.data.data);
                updateChartData(statsRes.data.data);
            }

            if (notificationsRes.data.success) {
                setNotifications(notificationsRes.data.data);
            }

            if (paymentsRes.data.success) {
                setPayments(paymentsRes.data.data);
            }
        } catch (error) {
            console.error("Dashboard error:", error.response?.data || error);
            showError("Failed to load dashboard data");
        } finally {
            setLoading(false);
        }
    };
    
    // Load available staff
    const loadAvailableStaff = async () => {
        try {
             const headers = {
                Authorization: `Bearer ${auth.accessToken}`
            };
              
            setLoadingStaff(true);
            const response = await axios.get(
                `${API_URL}pdashboard/staff/available`,
                {
                    headers
                }
            );
            
            if (response.data.success) {
                setAvailableStaff(response.data.data);
            }
        } catch (error) {
            console.error('Error loading staff:', error);
            showError('Failed to load staff list');
        } finally {
            setLoadingStaff(false);
        }
    };
    
    // Assign staff to booking
    const assignStaffToBooking = async () => {
        if (!selectedStaff || !currentBookingForStaff) {
            showError('Please select a staff member');
            return;
        }
        
        try {
            console.log("Assigning staff:", selectedStaff);
            console.log("To booking:", currentBookingForStaff);
            
            const response = await axios.post(
                `${API_URL}pdashboard/bookings/${currentBookingForStaff.Id}/assign-staff`,
                {
                    staffId: selectedStaff.id,
                    assignmentType: staffAssignmentType
                },
                {
                    headers: {
                        Authorization: `Bearer ${auth.accessToken}`
                    }
                }
            );
            
            if (response.data.success) {
                showSuccess(`Staff assigned for ${staffAssignmentType} successfully`);
                setShowStaffDialog(false);
                setSelectedStaff(null);
                setCurrentBookingForStaff(null);
                loadDashboardData();
            }
        } catch (error) {
            console.error('Error assigning staff:', error);
            showError(error.response?.data?.message || 'Failed to assign staff');
        }
    };
    
    // ==================== CANCELLATION FUNCTIONS ====================
    
    const openCancelDialog = (bookingId) => {
        const booking = bookings.find(b => (b.Id || b.id || b.bookingId) === bookingId);
        if (booking) {
            setCancelBookingId(bookingId);
            setSelectedBooking(booking);
            setRefundAmount(booking.totalamount || 0);
            setCancelReason('');
            setCancelReasonOther('');
            setSelectedCancelReason('');
            setCancellationNote('');
            setProcessRefund(true);
            setShowCancelDialog(true);
        }
    };
    
   
        const sendWhatsAppNotification = async (bookingId) => {
        const res = await fetch(`${API_URL}whats/notify-cancel`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ OrderId: bookingId })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message);
        return data;
    };

    const processCancellation = async () => {
        // Validate cancellation reason
        if (!selectedCancelReason) {
            showError('Please select a cancellation reason');
            return;
        }
        
        if (selectedCancelReason === 'Other' && !cancelReasonOther.trim()) {
            showError('Please provide details for the cancellation reason');
            return;
        }
        
        try {
            setCancellationLoading(true);
            
            const finalReason = selectedCancelReason === 'Other' 
                ? cancelReasonOther 
                : selectedCancelReason;
            
            const payload = {
                reason: finalReason,
                note: cancellationNote || finalReason,
                processRefund: processRefund,
                refundAmount: processRefund ? refundAmount : 0
            };
            
            const response = await axios.post(
                `${API_URL}pdashboard/bookings/${cancelBookingId}/cancel`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${auth.accessToken}`
                    }
                }
            );
            
            if (response.data.success) {
                showSuccess('Booking cancelled successfully');
                 await sendWhatsAppNotification(cancelBookingId);
                setShowCancelDialog(false);
                setCancelBookingId(null);
                setSelectedBooking(null);
                loadDashboardData(); // Refresh data
            } else {
                showError(response.data.message || 'Failed to cancel booking');
            }
        } catch (error) {
            console.error('Error cancelling booking:', error);
            showError(error.response?.data?.message || 'Failed to cancel booking');
        } finally {
            setCancellationLoading(false);
        }
    };
    
    // ==================== CHART FUNCTIONS ====================
    
    const initializeChart = () => {
        const documentStyle = getComputedStyle(document.documentElement);
        
        setChartOptions({
            plugins: {
                legend: {
                    labels: {
                        usePointStyle: true,
                        color: documentStyle.getPropertyValue('--text-color')
                    }
                }
            },
            scales: {
                x: {
                    ticks: {
                        color: documentStyle.getPropertyValue('--text-color-secondary')
                    },
                    grid: {
                        color: documentStyle.getPropertyValue('--surface-border')
                    }
                },
                y: {
                    beginAtZero: true,
                    ticks: {
                        color: documentStyle.getPropertyValue('--text-color-secondary')
                    },
                    grid: {
                        color: documentStyle.getPropertyValue('--surface-border')
                    }
                }
            }
        });
    };
    
    const updateChartData = (statsData) => {
        const documentStyle = getComputedStyle(document.documentElement);
        
        setChartData({
            labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'],
            datasets: [
                {
                    label: 'Bookings',
                    data: [65, 59, 80, 81, 56, 55, 40],
                    fill: true,
                    borderColor: documentStyle.getPropertyValue('--primary-500'),
                    backgroundColor: 'rgba(99, 102, 241, 0.1)',
                    tension: 0.4
                },
                {
                    label: 'Revenue',
                    data: [28, 48, 40, 19, 86, 27, 90],
                    fill: true,
                    borderColor: documentStyle.getPropertyValue('--green-500'),
                    backgroundColor: 'rgba(34, 197, 94, 0.1)',
                    tension: 0.4
                }
            ]
        });
    };
    
    // ==================== BOOKING MANAGEMENT ====================
    
    const filterBookings = () => {
        let filtered = [...bookings];
        
        if (statusFilter !== 'All') {
            filtered = filtered.filter(booking => {
                const bookingStatus = booking.Status || booking.status || booking.bookingStatus;
                return bookingStatus === statusFilter;
            });
        }
        
        if (searchText && searchText.trim() !== '') {
            const searchLower = searchText.toLowerCase().trim();
            filtered = filtered.filter(booking => {
                const ref = (booking.BookingReference || booking.bookingreference || booking.reference || '').toLowerCase();
                const pickup = (booking.PickupAddress || booking.pickupaddress || booking.pickupAddress || '').toLowerCase();
                const customer = (booking.CustomerName || booking.userInfo?.fullname || booking.customerName || '').toLowerCase();
                
                return ref.includes(searchLower) || 
                       pickup.includes(searchLower) || 
                       customer.includes(searchLower);
            });
        }
        
        setFilteredBookings(filtered);
    };
    
    const viewBookingDetails = (booking) => {
        setSelectedBooking(booking);
        setShowBookingDialog(true);
    };
    
    const updateBookingStatus = async (bookingId, newStatus) => {
        try {
             const headers = {
                Authorization: `Bearer ${auth.accessToken}`
            };
            const response = await axios.put(
                `${API_URL}pdashboard/bookings/${bookingId}/status`,
                { status: newStatus },
                headers
            );
            
            if (response.data.success) {
                showSuccess(`Booking status updated to ${newStatus}`);
                loadDashboardData();
                setShowBookingDialog(false);
            }
        } catch (error) {
            console.error('Error updating booking status:', error);
            showError('Failed to update booking status');
        }
    };
    
    const confirmAction = (type, bookingId) => {
        setActionType(type);
        setActionBookingId(bookingId);
        
        // If cancelling, open the cancel dialog instead of confirm dialog
        if (type === 'cancel') {
            openCancelDialog(bookingId);
            return;
        }
        
        setShowConfirmDialog(true);
    };
    
    const handleActionConfirm = () => {
        if (actionType === 'accept') {
            updateBookingStatus(actionBookingId, 'Confirmed');
        } else if (actionType === 'pickup') {
            updateBookingStatus(actionBookingId, 'PickedUp');
        } else if (actionType === 'process') {
            updateBookingStatus(actionBookingId, 'Processing');
        } else if (actionType === 'ready') {
            updateBookingStatus(actionBookingId, 'ReadyForDelivery');
        } else if (actionType === 'deliver') {
            updateBookingStatus(actionBookingId, 'OutForDelivery');
        } else if (actionType === 'complete') {
            updateBookingStatus(actionBookingId, 'Completed');
        }
        setShowConfirmDialog(false);
    };
    
    // ==================== NOTIFICATION FUNCTIONS ====================
    
    const markNotificationAsRead = async (notificationId) => {
        try {
            await axios.put(
                `${API_URL}pdashboard/notifications/${notificationId}/read`,
                {},
                { headers: { Authorization: `Bearer ${auth.accessToken}` } }
            );
            
            setNotifications(notifications.map(notif => 
                notif.Id === notificationId ? { ...notif, IsRead: true } : notif
            ));
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };
    
    const markAllNotificationsAsRead = async () => {
        try {
            await axios.put(
                `${API_URL}pdashboard/notifications/mark-all-read`,
                {},
                { headers: { Authorization: `Bearer ${auth.accessToken}` } }
            );
            
            setNotifications(notifications.map(notif => ({ ...notif, IsRead: true })));
            showSuccess('All notifications marked as read');
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    };
    
    // ==================== UI HELPERS ====================
    
    const getStatusSeverity = (status) => {
        return statusColors[status] || 'secondary';
    };
    
    const getPaymentSeverity = (status) => {
        return paymentColors[status] || 'secondary';
    };
    
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount);
    };
    
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };
    
    const showError = (message) => {
        if (toast.current) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: message,
                life: 5000
            });
        }
    };
    
    const showSuccess = (message) => {
        if (toast.current) {
            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: message,
                life: 5000
            });
        }
    };
    
    // ==================== RENDER COMPONENTS ====================
    
    const renderStatsCards = () => (
        <div className="grid">
            <div className="col-12 md:col-6 lg:col-3">
                <Card className="h-full">
                    <div className="flex justify-content-between align-items-center mb-3">
                        <div>
                            <span className="block text-500 font-medium mb-1">Total Bookings</span>
                            <div className="text-900 font-medium text-2xl">{stats.totalBookings}</div>
                        </div>
                        <div className="flex align-items-center justify-content-center border-round bg-blue-100" style={{ width: '3rem', height: '3rem' }}>
                            <i className="pi pi-shopping-cart text-blue-500 text-xl"></i>
                        </div>
                    </div>
                    <span className={`text-${stats.monthlyGrowth >= 0 ? 'green' : 'red'}-500 font-medium`}>
                        {stats.monthlyGrowth >= 0 ? '+' : ''}{stats.monthlyGrowth}%
                    </span>
                    <span className="text-500 ml-2">since last month</span>
                </Card>
            </div>
            
            <div className="col-12 md:col-6 lg:col-3">
                <Card className="h-full">
                    <div className="flex justify-content-between align-items-center mb-3">
                        <div>
                            <span className="block text-500 font-medium mb-1">Active Bookings</span>
                            <div className="text-900 font-medium text-2xl">{stats.activeBookings}</div>
                        </div>
                        <div className="flex align-items-center justify-content-center border-round bg-orange-100" style={{ width: '3rem', height: '3rem' }}>
                            <i className="pi pi-clock text-orange-500 text-xl"></i>
                        </div>
                    </div>
                    <div className="mt-3">
                        <ProgressBar value={(stats.activeBookings / stats.totalBookings) * 100 || 0} 
                            showValue={false} className="h-1rem" />
                        <div className="flex justify-content-between mt-2">
                            <span className="text-500 text-sm">Progress</span>
                            <span className="text-500 text-sm font-medium">
                                {Math.round((stats.activeBookings / stats.totalBookings) * 100) || 0}%
                            </span>
                        </div>
                    </div>
                </Card>
            </div>
            
            <div className="col-12 md:col-6 lg:col-3">
                <Card className="h-full">
                    <div className="flex justify-content-between align-items-center mb-3">
                        <div>
                            <span className="block text-500 font-medium mb-1">Total Revenue</span>
                            <div className="text-900 font-medium text-2xl">{formatCurrency(stats.totalRevenue)}</div>
                        </div>
                        <div className="flex align-items-center justify-content-center border-round bg-cyan-100" style={{ width: '3rem', height: '3rem' }}>
                            <i className="pi pi-wallet text-cyan-500 text-xl"></i>
                        </div>
                    </div>
                    <div className="flex align-items-center mt-3">
                        <Rating value={stats.avgRating} readOnly cancel={false} stars={5} />
                        <span className="text-500 ml-2">({stats.avgRating.toFixed(1)})</span>
                    </div>
                </Card>
            </div>
            
            <div className="col-12 md:col-6 lg:col-3">
                <Card className="h-full">
                    <div className="flex justify-content-between align-items-center mb-3">
                        <div>
                            <span className="block text-500 font-medium mb-1">Pending Payments</span>
                            <div className="text-900 font-medium text-2xl">{formatCurrency(stats.pendingPayments)}</div>
                        </div>
                        <div className="flex align-items-center justify-content-center border-round bg-purple-100" style={{ width: '3rem', height: '3rem' }}>
                            <i className="pi pi-money-bill text-purple-500 text-xl"></i>
                        </div>
                    </div>
                    <div className="mt-3">
                        <Badge value={`${stats.completedBookings} Completed`} severity="success" className="mr-2" />
                        <Badge value={`${stats.cancelledBookings} Cancelled`} severity="warning" />
                    </div>
                </Card>
            </div>
        </div>
    );
    
    const renderBookingActions = (booking) => {
        const actions = [];
        
        const normalizedBooking = {
            Id: booking.Id || booking.id || booking.bookingId,
            Status: booking.Status || booking.status || booking.bookingStatus,
            totalamount: booking.totalamount || booking.TotalAmount || 0,
            PaymentStatus: booking.paymentstatus || booking.PaymentStatus || 'Pending'
        };
        
        // Check if booking can be cancelled (before pickup)
        const canCancel = ['Pending', 'Confirmed'].includes(normalizedBooking.Status);
        
        switch(normalizedBooking.Status) {
            case 'Pending':
                actions.push(
                    <Button 
                        key="accept"
                        label="Accept"
                        icon="pi pi-check"
                        className="p-button-success p-button-sm mr-2"
                        onClick={() => confirmAction('accept', normalizedBooking.Id)}
                    />,
                    <Button 
                        key="cancel"
                        label="Cancel Order"
                        icon="pi pi-times"
                        className="p-button-danger p-button-sm mr-2"
                        onClick={() => confirmAction('cancel', normalizedBooking.Id)}
                    />
                );
                break;
                
            case 'Confirmed':
                actions.push(
                    <Button 
                        key="assignPickup"
                        label="Assign Pickup Staff"
                        icon="pi pi-user-plus"
                        className="p-button-info p-button-sm mr-2"
                        onClick={() => {
                            setStaffAssignmentType('pickup');
                            setCurrentBookingForStaff(normalizedBooking);
                            loadAvailableStaff();
                            setShowStaffDialog(true);
                        }}
                    />,
                    <Button 
                        key="pickup"
                        label="Pick Up"
                        icon="pi pi-box"
                        className="p-button-primary p-button-sm mr-2"
                        onClick={() => confirmAction('pickup', normalizedBooking.Id)}
                    />,
                    <Button 
                        key="cancel"
                        label="Cancel Order"
                        icon="pi pi-times"
                        className="p-button-danger p-button-sm"
                        onClick={() => confirmAction('cancel', normalizedBooking.Id)}
                    />
                );
                break;
                
            case 'PickedUp':
                actions.push(
                    <Button 
                        key="process"
                        label="Start Processing"
                        icon="pi pi-cog"
                        className="p-button-info p-button-sm mr-2"
                        onClick={() => confirmAction('process', normalizedBooking.Id)}
                    />
                );
                break;
                
            case 'Processing':
                actions.push(
                    <Button 
                        key="ready"
                        label="Ready for Delivery"
                        icon="pi pi-check-circle"
                        className="p-button-help p-button-sm"
                        onClick={() => confirmAction('ready', normalizedBooking.Id)}
                    />
                );
                break;
                
            case 'ReadyForDelivery':
                actions.push(
                    <Button 
                        key="assignDelivery"
                        label="Assign Delivery Staff"
                        icon="pi pi-user-plus"
                        className="p-button-warning p-button-sm mr-2"
                        onClick={() => {
                            setStaffAssignmentType('delivery');
                            setCurrentBookingForStaff(normalizedBooking);
                            loadAvailableStaff();
                            setShowStaffDialog(true);
                        }}
                    />,
                    <Button 
                        key="deliver"
                        label="Out for Delivery"
                        icon="pi pi-truck"
                        className="p-button-warning p-button-sm"
                        onClick={() => confirmAction('deliver', normalizedBooking.Id)}
                    />
                );
                break;
                
            case 'OutForDelivery':
                actions.push(
                    <Button 
                        key="complete"
                        label="Mark Delivered"
                        icon="pi pi-check"
                        className="p-button-success p-button-sm"
                        onClick={() => confirmAction('complete', normalizedBooking.Id)}
                    />
                );
                break;
        }
        
        actions.push(
            <Button 
                key="view"
                label="View"
                icon="pi pi-eye"
                className="p-button-secondary p-button-sm"
                onClick={() => viewBookingDetails(booking)}
            />
        );
        
        return (
            <div className="flex flex-wrap gap-1">
                {actions}
            </div>
        );
    };
    
    const renderBookingsTable = () => (
        <Card title="Recent Bookings" className="h-full">
            <div className="flex justify-content-between mb-4">
                <div className="flex gap-3">
                    <Dropdown 
                        value={statusFilter}
                        options={statusOptions}
                        onChange={(e) => setStatusFilter(e.value)}
                        placeholder="Filter by Status"
                        className="w-full md:w-14rem"
                    />
                    <span className="p-input-icon-left">
                        <i className="pi pi-search" />
                        <InputText 
                            placeholder="Search bookings..." 
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            className="w-full md:w-20rem"
                        />
                    </span>
                </div>
                <Button 
                    label="Refresh"
                    icon="pi pi-refresh"
                    className="p-button-outlined"
                    onClick={loadDashboardData}
                />
            </div>
            
            {loading ? (
                <div className="text-center p-6">
                    <ProgressBar mode="indeterminate" style={{ width: '50px', height: '50px' }} />
                    <p className="mt-3">Loading bookings...</p>
                </div>
            ) : (
                <DataTable 
                    value={filteredBookings}
                    paginator 
                    rows={10}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    size="small"
                    emptyMessage="No bookings found"
                >
                    <Column field="bookingreference" header="Reference" sortable />
                    <Column 
                        field="pickupdate" 
                        header="Pickup Date" 
                        sortable 
                        body={(rowData) => formatDate(rowData.pickupdate)}
                    />
                    <Column 
                        field="userInfo.fullname" 
                        header="Customer" 
                        sortable 
                        body={(rowData) => rowData.userInfo?.fullname || 'N/A'}
                    />
                    <Column 
                        field="status" 
                        header="Status" 
                        sortable 
                        body={(rowData) => (
                            <Badge 
                                value={rowData.status} 
                                severity={getStatusSeverity(rowData.status)}
                            />
                        )}
                    />
                    <Column 
                        field="paymentstatus" 
                        header="Payment" 
                        sortable 
                        body={(rowData) => (
                            <Badge 
                                value={rowData.paymentstatus} 
                                severity={getPaymentSeverity(rowData.paymentstatus)}
                            />
                        )}
                    />
                    <Column 
                        field="totalamount" 
                        header="Amount" 
                        sortable 
                        body={(rowData) => formatCurrency(rowData.totalamount)}
                    />
                    <Column 
                        header="Actions" 
                        body={renderBookingActions}
                        style={{ width: '350px' }}
                    />
                </DataTable>
            )}
        </Card>
    );
    
    const renderBookingDetailsDialog = () => (
        <Dialog 
            header="Booking Details" 
            visible={showBookingDialog} 
            style={{ width: '800px', maxWidth: '90vw' }}
            onHide={() => setShowBookingDialog(false)}
        >
            {selectedBooking && (
                <div className="grid">
                    <div className="col-12 md:col-6">
                        <h4 className="mt-0">Booking Information</h4>
                        <div className="surface-50 p-3 border-round">
                            <p><strong>Reference:</strong> {selectedBooking.bookingreference}</p>
                            <p><strong>Status:</strong> 
                                <Badge value={selectedBooking.status} severity={getStatusSeverity(selectedBooking.status)} className="ml-2" />
                            </p>
                            <p><strong>Created:</strong> {formatDate(selectedBooking.createdat)}</p>
                            <p><strong>Pickup Date:</strong> {formatDate(selectedBooking.pickupdate)}</p>
                            <p><strong>Delivery Date:</strong> {formatDate(selectedBooking.deliverydate)}</p>
                            <p><strong>Total Amount:</strong> {formatCurrency(selectedBooking.totalamount)}</p>
                            <p><strong>Payment Status:</strong> 
                                <Badge value={selectedBooking.paymentstatus} severity={getPaymentSeverity(selectedBooking.paymentstatus)} className="ml-2" />
                            </p>
                            
                            {(selectedBooking.pickupStaffId || selectedBooking.deliveryStaffId) && (
                                <>
                                    <Divider />
                                    <h4 className="mt-2 mb-2">Staff Assignments</h4>
                                    {selectedBooking.pickupStaffId && (
                                        <p><strong>Pickup Staff:</strong> {selectedBooking.pickupStaffName || 'Assigned'}</p>
                                    )}
                                    {selectedBooking.deliveryStaffId && (
                                        <p><strong>Delivery Staff:</strong> {selectedBooking.deliveryStaffName || 'Assigned'}</p>
                                    )}
                                </>
                            )}
                        </div>
                        
                        <h4 className="mt-4">Customer Information</h4>
                        <div className="surface-50 p-3 border-round">
                            {selectedBooking.userInfo ? (
                                <>
                                    <p><strong>Name:</strong> {selectedBooking.userInfo.fullname}</p>
                                    <p><strong>Phone:</strong> {selectedBooking.userInfo.phonenumber}</p>
                                    <p><strong>Email:</strong> {selectedBooking.userInfo.email}</p>
                                </>
                            ) : (
                                <p>No customer information available</p>
                            )}
                        </div>
                    </div>
                    
                    <div className="col-12 md:col-6">
                        <h4 className="mt-0">Address Details</h4>
                        <div className="surface-50 p-3 border-round">
                            <p><strong>Pickup Address:</strong></p>
                            <p className="text-sm">{selectedBooking.pickupaddress}</p>
                            <Divider />
                            <p><strong>Delivery Address:</strong></p>
                            <p className="text-sm">{selectedBooking.deliveryaddress}</p>
                        </div>
                        
                        <h4 className="mt-4">Items</h4>
                        <div className="surface-50 p-3 border-round">
                            {selectedBooking.items && selectedBooking.items.length > 0 ? (
                                <div className="grid">
                                    {selectedBooking.items.map((item, index) => (
                                        <div key={item.id || index} className="col-12 mb-2">
                                            <div className="flex justify-content-between">
                                                <span><strong>{item.quantity}x {item.itemtype}</strong></span>
                                                <span>{formatCurrency(item.totalprice)}</span>
                                            </div>
                                            <div className="text-sm text-500">
                                                <div>Unit Price: {formatCurrency(item.unitprice)}</div>
                                                {item.treatment && (
                                                    <div>Treatment: {item.treatment}</div>
                                                )}
                                                {item.instructions && (
                                                    <div>Instructions: {item.instructions}</div>
                                                )}
                                            </div>
                                            {index < selectedBooking.items.length - 1 && <Divider />}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p>No items found</p>
                            )}
                        </div>
                    </div>
                    
                    <div className="col-12">
                        <Divider />
                        <div className="flex justify-content-end gap-2">
                            {renderBookingActions(selectedBooking)}
                        </div>
                    </div>
                </div>
            )}
        </Dialog>
    );
    
    const renderStaffAssignmentDialog = () => (
        <Dialog 
            header={`Assign Staff for ${staffAssignmentType === 'pickup' ? 'Pickup' : 'Delivery'}`}
            visible={showStaffDialog} 
            style={{ width: '500px' }}
            onHide={() => {
                setShowStaffDialog(false);
                setSelectedStaff(null);
                setCurrentBookingForStaff(null);
            }}
            footer={
                <div>
                    <Button 
                        label="Cancel" 
                        icon="pi pi-times" 
                        onClick={() => {
                            setShowStaffDialog(false);
                            setSelectedStaff(null);
                            setCurrentBookingForStaff(null);
                        }} 
                        className="p-button-text"
                    />
                    <Button 
                        label="Assign" 
                        icon="pi pi-check" 
                        onClick={assignStaffToBooking} 
                        disabled={!selectedStaff || loadingStaff}
                        autoFocus
                    />
                </div>
            }
        >
            {loadingStaff ? (
                <div className="text-center p-4">
                    <ProgressBar mode="indeterminate" style={{ width: '50px', height: '50px' }} />
                    <p className="mt-3">Loading staff...</p>
                </div>
            ) : availableStaff.length === 0 ? (
                <div className="text-center p-4">
                    <i className="pi pi-users text-4xl text-gray-400 mb-3"></i>
                    <p>No available staff members found</p>
                    <p className="text-sm text-500">Please add staff members first</p>
                </div>
            ) : (
                <div className="p-fluid">
                    <div className="field">
                        <label htmlFor="staff">Select Staff Member</label>
                        <Dropdown
                            id="staff"
                            value={selectedStaff}
                            options={availableStaff}
                            onChange={(e) => {
                                console.log("Selected staff:", e.value);
                                setSelectedStaff(e.value);
                            }}
                            optionLabel="name"
                            placeholder="Choose a staff member"
                            className="w-full"
                            filter
                            showClear
                            itemTemplate={(option) => {
                                return (
                                    <div className="flex align-items-center gap-2">
                                        <div>
                                            <div>{option.name}</div>
                                            <div className="text-sm text-500">{option.role} - {option.email}</div>
                                        </div>
                                    </div>
                                );
                            }}
                        />
                    </div>
                    
                    {selectedStaff && (
                        <div className="mt-4 p-3 surface-50 border-round">
                            <h4 className="mt-0 mb-2">Staff Details</h4>
                            <div className="grid">
                                <div className="col-12">
                                    <p className="mb-2">
                                        <i className="pi pi-user mr-2 text-primary"></i>
                                        <strong>Name:</strong> {selectedStaff.name}
                                    </p>
                                    <p className="mb-2">
                                        <i className="pi pi-briefcase mr-2 text-primary"></i>
                                        <strong>Role:</strong> {selectedStaff.role}
                                    </p>
                                    <p className="mb-2">
                                        <i className="pi pi-phone mr-2 text-primary"></i>
                                        <strong>Phone:</strong> {selectedStaff.phone}
                                    </p>
                                    <p className="mb-2">
                                        <i className="pi pi-envelope mr-2 text-primary"></i>
                                        <strong>Email:</strong> {selectedStaff.email}
                                    </p>
                                    <p className="mb-2">
                                        <i className="pi pi-calendar mr-2 text-primary"></i>
                                        <strong>Current Assignments:</strong> 
                                        <Badge value={selectedStaff.currentAssignments || 0} severity="info" className="ml-2" />
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </Dialog>
    );
    
    // ==================== CANCELLATION DIALOG ====================
    
    const renderCancelDialog = () => (
        <Dialog 
            header="Cancel Order"
            visible={showCancelDialog} 
            style={{ width: '550px', maxWidth: '90vw' }}
            onHide={() => {
                if (!cancellationLoading) {
                    setShowCancelDialog(false);
                    setCancelBookingId(null);
                    setSelectedBooking(null);
                }
            }}
            footer={
                <div className="flex justify-content-end gap-2">
                    <Button 
                        label="Close" 
                        icon="pi pi-times" 
                        onClick={() => {
                            setShowCancelDialog(false);
                            setCancelBookingId(null);
                            setSelectedBooking(null);
                        }} 
                        className="p-button-text"
                        disabled={cancellationLoading}
                    />
                    <Button 
                        label="Cancel Order" 
                        icon="pi pi-times-circle" 
                        onClick={processCancellation} 
                        className="p-button-danger"
                        disabled={cancellationLoading || !selectedCancelReason}
                        loading={cancellationLoading}
                    />
                </div>
            }
            closable={!cancellationLoading}
        >
            {selectedBooking && (
                <div className="p-fluid">
                    {/* Order Summary */}
                    <div className="surface-50 p-3 border-round mb-4">
                        <div className="flex justify-content-between align-items-center mb-2">
                            <div>
                                <span className="font-bold">Order #</span>
                                <span className="ml-2">{selectedBooking.bookingreference}</span>
                            </div>
                            <Badge 
                                value={selectedBooking.status} 
                                severity={getStatusSeverity(selectedBooking.status)}
                            />
                        </div>
                        <div className="flex justify-content-between">
                            <span className="text-500">Amount:</span>
                            <span className="font-bold">{formatCurrency(selectedBooking.totalamount)}</span>
                        </div>
                        <div className="flex justify-content-between">
                            <span className="text-500">Customer:</span>
                            <span>{selectedBooking.userInfo?.fullname || 'N/A'}</span>
                        </div>
                        <div className="flex justify-content-between">
                            <span className="text-500">Pickup Date:</span>
                            <span>{formatDate(selectedBooking.pickupdate)}</span>
                        </div>
                    </div>
                    
                    <Divider />
                    
                    {/* Cancellation Reason */}
                    <div className="field">
                        <label className="font-bold block mb-2">
                            Cancellation Reason <span className="text-red-500">*</span>
                        </label>
                        <div className="grid">
                            {cancellationReasons.map((reason) => (
                                <div key={reason.value} className="col-12 md:col-6 mb-2">
                                    <div className="flex align-items-center">
                                        <RadioButton
                                            inputId={`reason-${reason.value}`}
                                            name="cancelReason"
                                            value={reason.value}
                                            onChange={(e) => {
                                                setSelectedCancelReason(e.value);
                                                if (e.value !== 'Other') {
                                                    setCancelReasonOther('');
                                                }
                                            }}
                                            checked={selectedCancelReason === reason.value}
                                            disabled={cancellationLoading}
                                        />
                                        <label htmlFor={`reason-${reason.value}`} className="ml-2">
                                            {reason.label}
                                        </label>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    {/* Other Reason Input */}
                    {selectedCancelReason === 'Other' && (
                        <div className="field mt-2">
                            <label htmlFor="otherReason" className="font-medium">
                                Please specify <span className="text-red-500">*</span>
                            </label>
                            <InputTextarea
                                id="otherReason"
                                value={cancelReasonOther}
                                onChange={(e) => setCancelReasonOther(e.target.value)}
                                rows={2}
                                placeholder="Please provide details for cancellation..."
                                disabled={cancellationLoading}
                                className="w-full"
                                autoFocus
                            />
                        </div>
                    )}
                    
                    {/* Additional Notes */}
                    <div className="field mt-2">
                        <label htmlFor="cancellationNote" className="font-medium">
                            Additional Notes (Optional)
                        </label>
                        <InputTextarea
                            id="cancellationNote"
                            value={cancellationNote}
                            onChange={(e) => setCancellationNote(e.target.value)}
                            rows={3}
                            placeholder="Add any additional notes about the cancellation..."
                            disabled={cancellationLoading}
                            className="w-full"
                        />
                    </div>
                    
                    <Divider />
                    
                    {/* Refund Options */}
                    <div className="field">
                        <div className="flex align-items-center mb-3">
                            <div className="flex align-items-center">
                                <RadioButton
                                    inputId="refundYes"
                                    name="processRefund"
                                    value={true}
                                    onChange={(e) => setProcessRefund(e.value)}
                                    checked={processRefund === true}
                                    disabled={cancellationLoading}
                                />
                                <label htmlFor="refundYes" className="ml-2">
                                    Process Refund
                                </label>
                            </div>
                            <div className="flex align-items-center ml-4">
                                <RadioButton
                                    inputId="refundNo"
                                    name="processRefund"
                                    value={false}
                                    onChange={(e) => setProcessRefund(e.value)}
                                    checked={processRefund === false}
                                    disabled={cancellationLoading}
                                />
                                <label htmlFor="refundNo" className="ml-2">
                                    No Refund
                                </label>
                            </div>
                        </div>
                        
                        {processRefund && (
                            <div className="field">
                                <label htmlFor="refundAmount" className="font-medium">
                                    Refund Amount
                                </label>
                                <InputNumber
                                    id="refundAmount"
                                    value={refundAmount}
                                    onValueChange={(e) => setRefundAmount(e.value)}
                                    mode="currency"
                                    currency="INR"
                                    locale="en-IN"
                                    min={0}
                                    max={selectedBooking.totalamount}
                                    disabled={cancellationLoading}
                                    className="w-full"
                                />
                                <small className="text-500">
                                    Max refund: {formatCurrency(selectedBooking.totalamount)}
                                </small>
                            </div>
                        )}
                    </div>
                    
                    {/* Warning Message */}
                    <div className="mt-3 p-3 bg-red-50 border-round border-1 border-red-200">
                        <div className="flex align-items-start gap-2">
                            <i className="pi pi-exclamation-triangle text-red-500 mt-1"></i>
                            <div>
                                <span className="font-bold text-red-700">Warning:</span>
                                <span className="text-red-600 ml-2">
                                    This action cannot be undone. The order will be cancelled and 
                                    {processRefund && ' a refund will be processed for the customer.'}
                                    {!processRefund && ' no refund will be issued.'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Dialog>
    );
    
    // ==================== NOTIFICATIONS PANEL ====================
    
    const renderNotificationsPanel = () => (
        <Card title="Notifications" className="h-full">
            <style>{`
                .notification-item {
                    transition: all 0.2s ease;
                    border-left: 3px solid transparent;
                }
                .notification-item.unread {
                    border-left-color: var(--primary-color);
                    background: linear-gradient(to right, rgba(33, 150, 243, 0.05), transparent);
                }
                .notification-item:hover {
                    background: var(--surface-100) !important;
                    transform: translateX(2px);
                }
                .notification-badge {
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                }
            `}</style>
            
            <div className="flex justify-content-between align-items-center mb-3">
                <div className="flex align-items-center gap-2">
                    <i className="pi pi-bell text-primary" style={{ fontSize: '1.2rem' }}></i>
                    <span className="font-semibold">Latest Updates</span>
                </div>
                <div className="flex align-items-center gap-2">
                    <Button 
                        icon="pi pi-check"
                        className="p-button-rounded p-button-text p-button-sm"
                        onClick={markAllNotificationsAsRead}
                        disabled={notifications.filter(n => !n.isRead).length === 0}
                        tooltip="Mark all as read"
                        tooltipOptions={{ position: 'left' }}
                    />
                    <Badge 
                        value={notifications.filter(n => !n.isRead).length} 
                        severity="danger" 
                        className="notification-badge"
                    />
                </div>
            </div>
            
            {!notifications || notifications.length === 0 ? (
                <div className="flex flex-column align-items-center justify-content-center p-4" style={{ minHeight: '300px' }}>
                    <i className="pi pi-bell-slash text-5xl text-gray-300 mb-3"></i>
                    <p className="text-gray-500 text-lg mb-2">All caught up!</p>
                    <p className="text-gray-400 text-sm">No new notifications</p>
                </div>
            ) : (
                <div className="flex flex-column gap-2">
                    {notifications.slice(0, 5).map(notification => (
                        <div 
                            key={notification.id}
                            className={`notification-item p-3 border-round surface-card cursor-pointer 
                                ${!notification.isRead ? 'unread' : ''}`}
                            onClick={() => markNotificationAsRead(notification.id)}
                            style={{ 
                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                borderBottom: '1px solid var(--surface-200)'
                            }}
                        >
                            <div className="flex align-items-start gap-3">
                                <div className={`flex-shrink-0 border-circle p-2 
                                    ${getNotificationTypeColor(notification.type)}`}
                                    style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <i className={`pi ${getNotificationTypeIcon(notification.type)} text-sm`}></i>
                                </div>
                                
                                <div className="flex-grow-1">
                                    <div className="flex justify-content-between align-items-start mb-1">
                                        <div>
                                            <span className="font-semibold text-900">{notification.title}</span>
                                            {notification.type && (
                                                <Badge 
                                                    value={notification.type} 
                                                    severity={getNotificationTypeSeverity(notification.type)}
                                                    className="ml-2 text-xs"
                                                />
                                            )}
                                        </div>
                                        <small className="text-500 whitespace-nowrap ml-2">
                                            {formatRelativeTime(notification.sentat)}
                                        </small>
                                    </div>
                                    
                                    <p className="text-sm text-600 m-0 line-height-2">
                                        {notification.message}
                                    </p>
                                    
                                    {notification.actionurl && (
                                        <div className="mt-2">
                                            <Button 
                                                label="View Details"
                                                icon="pi pi-arrow-right"
                                                className="p-button-text p-button-sm p-0"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    window.location.href = notification.actionurl;
                                                }}
                                                style={{ color: 'var(--primary-color)' }}
                                            />
                                        </div>
                                    )}
                                </div>
                                
                                {!notification.isRead && (
                                    <span 
                                        className="flex-shrink-0 bg-primary border-circle"
                                        style={{ width: '8px', height: '8px', marginTop: '8px' }}
                                    ></span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            
            {notifications && notifications.length > 5 && (
                <div className="mt-4 text-center border-top-1 surface-200 pt-3">
                    <Button 
                        label={`View All (${notifications.length})`}
                        icon="pi pi-list"
                        className="p-button-text p-button-sm"
                        onClick={() => window.location.href = '/partner/notifications'}
                    />
                </div>
            )}
        </Card>
    );
    
    const getNotificationTypeColor = (type) => {
        const colorMap = {
            'Payment': 'bg-green-100 text-green-600',
            'Booking': 'bg-blue-100 text-blue-600',
            'Alert': 'bg-yellow-100 text-yellow-600',
            'System': 'bg-purple-100 text-purple-600',
            'Reminder': 'bg-orange-100 text-orange-600',
            'Update': 'bg-cyan-100 text-cyan-600'
        };
        return colorMap[type] || 'bg-gray-100 text-gray-600';
    };
    
    const getNotificationTypeIcon = (type) => {
        const iconMap = {
            'Payment': 'pi-credit-card',
            'Booking': 'pi-calendar',
            'Alert': 'pi-exclamation-triangle',
            'System': 'pi-cog',
            'Reminder': 'pi-bell',
            'Update': 'pi-refresh'
        };
        return iconMap[type] || 'pi-info-circle';
    };
    
    const getNotificationTypeSeverity = (type) => {
        const severityMap = {
            'Payment': 'success',
            'Booking': 'info',
            'Alert': 'warning',
            'System': 'secondary',
            'Reminder': 'info',
            'Update': 'info'
        };
        return severityMap[type] || 'info';
    };
    
    const formatRelativeTime = (dateString) => {
        if (!dateString) return '';
        
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays}d ago`;
        
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric'
        });
    };
    
    const renderPaymentsTable = () => (
        <Card title="Recent Payments" className="h-full">
            {payments.length === 0 ? (
                <div className="text-center p-4">
                    <i className="pi pi-credit-card text-4xl text-gray-400 mb-3"></i>
                    <p>No payments yet</p>
                </div>
            ) : (
                <DataTable value={payments} size="small">
                    <Column field="paymentreference" header="Reference" />
                    <Column 
                        field="periodstart" 
                        header="Period" 
                        body={(rowData) => (
                            <span>
                                {new Date(rowData.periodstart).toLocaleDateString()} - 
                                {new Date(rowData.periodend).toLocaleDateString()}
                            </span>
                        )}
                    />
                    <Column 
                        field="totalbookings" 
                        header="Bookings" 
                    />
                    <Column 
                        field="partnerearnings" 
                        header="Earnings" 
                        body={(rowData) => formatCurrency(rowData.partnerearnings)}
                    />
                    <Column 
                        field="status" 
                        header="Status" 
                        body={(rowData) => (
                            <Badge value={rowData.status} severity={
                                rowData.status === 'Paid' ? 'success' : 
                                rowData.status === 'Processing' ? 'warning' : 'danger'
                            } />
                        )}
                    />
                    <Column 
                        field="paiddate" 
                        header="Paid Date" 
                        body={(rowData) => rowData.paiddate ? formatDate(rowData.paiddate) : 'Pending'}
                    />
                    <Column 
                        header="Actions"
                        body={(rowData) => (
                            <Button 
                                icon="pi pi-eye" 
                                className="p-button-rounded p-button-text"
                                onClick={() => viewPaymentDetails(rowData.id)}
                            />
                        )}
                    />
                </DataTable>
            )}
            
            <div className="mt-3 text-center">
                <Button 
                    label="View Payment History"
                    icon="pi pi-history"
                    className="p-button-outlined"
                    onClick={() => window.location.href = '/partnerpaymentvw'}
                />
            </div>
        </Card>
    );
    
    const renderAnalyticsChart = () => (
        <Card title="Performance Analytics">
            <div className="flex justify-content-between mb-4">
                <div>
                    <h3 className="mt-0 mb-1">Booking Trends</h3>
                    <p className="text-500 mt-0">Last 7 months performance</p>
                </div>
                <div className="flex gap-2">
                    <Button 
                        label="Last 30 Days"
                        className="p-button-outlined p-button-sm"
                        onClick={() => setDateRange({
                            start: new Date(new Date().setDate(new Date().getDate() - 30)),
                            end: new Date()
                        })}
                    />
                    <Button 
                        label="Last 3 Months"
                        className="p-button-outlined p-button-sm"
                        onClick={() => setDateRange({
                            start: new Date(new Date().setMonth(new Date().getMonth() - 3)),
                            end: new Date()
                        })}
                    />
                </div>
            </div>
            
            {chartData ? (
                <Chart type="line" data={chartData} options={chartOptions} style={{ height: '300px' }} />
            ) : (
                <div className="text-center p-6">
                    <ProgressBar mode="indeterminate" style={{ width: '50px', height: '50px' }} />
                    <p className="mt-3">Loading chart data...</p>
                </div>
            )}
            
            <Divider />
            
            <div className="grid">
                <div className="col-6 md:col-3 text-center">
                    <div className="text-900 font-bold text-2xl">{stats.totalBookings}</div>
                    <div className="text-500">Total Bookings</div>
                </div>
                <div className="col-6 md:col-3 text-center">
                    <div className="text-900 font-bold text-2xl">{formatCurrency(stats.totalRevenue)}</div>
                    <div className="text-500">Total Revenue</div>
                </div>
                <div className="col-6 md:col-3 text-center">
                    <div className="text-900 font-bold text-2xl">{stats.avgRating.toFixed(1)}</div>
                    <div className="text-500">Avg Rating</div>
                </div>
                <div className="col-6 md:col-3 text-center">
                    <div className="text-900 font-bold text-2xl">{stats.completedBookings}</div>
                    <div className="text-500">Completed</div>
                </div>
            </div>
        </Card>
    );
    
    // ==================== MAIN RENDER ====================
    
    return (
        <div className="p-fluid">
            <Toast ref={toast} position="top-right" />
            
            <ConfirmDialog 
                visible={showConfirmDialog}
                onHide={() => setShowConfirmDialog(false)}
                message="Are you sure you want to perform this action?"
                header="Confirmation"
                icon="pi pi-exclamation-triangle"
                accept={handleActionConfirm}
                reject={() => setShowConfirmDialog(false)}
            />
            
            {renderStaffAssignmentDialog()}
            {renderBookingDetailsDialog()}
            {renderCancelDialog()}
            
            {/* Header */}
            <Card className="mb-4">
                <div className="flex justify-content-between align-items-center">
                    <div>
                        <h2 className="mt-0 mb-1">Partner Dashboard</h2>
                        <p className="text-500 mt-0">
                            Welcome back, {partnerData?.ownername || 'Partner'}!
                            {partnerData?.shopname && ` - ${partnerData.shopname}`}
                        </p>
                    </div>
                    <div className="flex align-items-center gap-3">
                        <Avatar 
                            icon="pi pi-user" 
                            size="large" 
                            shape="circle" 
                            className="bg-primary"
                        />
                        <div>
                            <div className="font-bold">{partnerData?.shopname || 'Shop'}</div>
                            <div className="text-sm text-500">{partnerData?.status || 'Active'}</div>
                        </div>
                    </div>
                </div>
                
                <Divider />
                
                <div className="flex flex-wrap gap-3">
                    <Chip label={`Rating: ${partnerData?.rating || 0.0}`} icon="pi pi-star" />
                    <Chip label={`Commission: ${partnerData?.commissionrate || 0}%`} icon="pi pi-percentage" />
                    <Chip label={`Capacity: ${partnerData?.currentload || 0}/${partnerData?.CapacityPerDay || 0}`} icon="pi pi-box" />
                    <Chip label={`Reviews: ${partnerData?.totalreviews || 0}`} icon="pi pi-comment" />
                </div>
            </Card>
            
            {/* Stats Cards */}
            {renderStatsCards()}
            
            {/* Main Content */}
            <div className="grid mt-4">
                <div className="col-12 lg:col-8">
                    <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
                        <TabPanel header="Bookings">
                            {renderBookingsTable()}
                        </TabPanel>
                        <TabPanel header="Analytics">
                            {renderAnalyticsChart()}
                        </TabPanel>
                        <TabPanel header="Performance">
                            <Card title="Shop Performance">
                                <div className="grid">
                                    <div className="col-12 md:col-6">
                                        <h4>Capacity Utilization</h4>
                                        <div className="mt-3">
                                            <div className="flex justify-content-between mb-2">
                                                <span>Current Load</span>
                                                <span>{partnerData?.currentload || 0}/{partnerData?.capacityperday || 0}</span>
                                            </div>
                                            <ProgressBar 
                                                value={((partnerData?.currentload || 0) / (partnerData?.capacityperday || 1)) * 100}
                                                showValue={false}
                                                className="h-1rem mb-3"
                                            />
                                            
                                            <div className="flex justify-content-between mb-2">
                                                <span>Available Slots</span>
                                                <span>{((partnerData?.capacityperday || 0) - (partnerData?.currentload || 0))}</span>
                                            </div>
                                            <ProgressBar 
                                                value={(((partnerData?.capacityperday || 0) - (partnerData?.currentload || 0)) / (partnerData?.capacityperday || 1)) * 100}
                                                showValue={false}
                                                className="h-1rem"
                                                color="green"
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="col-12 md:col-6">
                                        <h4>Service Metrics</h4>
                                        <div className="mt-3 space-y-3">
                                            <div>
                                                <div className="flex justify-content-between">
                                                    <span>On-time Delivery</span>
                                                    <span>{partnerData?.ontimedeliveryrate || 0}%</span>
                                                </div>
                                                <ProgressBar 
                                                    value={partnerData?.ontimedeliveryrate || 0}
                                                    showValue={false}
                                                    className="h-1rem"
                                                />
                                            </div>
                                            
                                            <div>
                                                <div className="flex justify-content-between">
                                                    <span>Customer Satisfaction</span>
                                                    <span>{partnerData?.customersatisfaction || 0}%</span>
                                                </div>
                                                <ProgressBar 
                                                    value={partnerData?.customersatisfaction || 0}
                                                    showValue={false}
                                                    className="h-1rem"
                                                    color="green"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        </TabPanel>
                    </TabView>
                </div>
                
                <div className="col-12 lg:col-4">
                    <div className="space-y-4">
                        {renderNotificationsPanel()}
                        {renderPaymentsTable()}
                    </div>
                </div>
            </div>
            
            {/* Quick Actions */}
            <Card className="mt-4">
                <h3 className="mt-0">Quick Actions</h3>
                <div className="grid">
                    <div className="col-6 md:col-3">
                        <Button 
                            label="New Booking"
                            icon="pi pi-plus"
                            className="p-button-outlined w-full mb-2"
                            onClick={() => window.location.href = 'bookingquick'}
                        />
                    </div>
                    <div className="col-6 md:col-3">
                        <Button 
                            label="Update Profile"
                            icon="pi pi-user-edit"
                            className="p-button-outlined w-full mb-2"
                            onClick={() => window.location.href = '/account/edit'}
                        />
                    </div>
                    <div className="col-6 md:col-3">
                        <Button 
                            label="View Calendar"
                            icon="pi pi-calendar"
                            className="p-button-outlined w-full mb-2"
                            onClick={() => window.location.href = '/bcalender'}
                        />
                    </div>
                    <div className="col-6 md:col-3">
                        <Button 
                            label="Support"
                            icon="pi pi-question-circle"
                            className="p-button-outlined w-full mb-2"
                            onClick={() => window.location.href = '/support'}
                        />
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default PartnerDashboard;